import { type ReactNode } from "react";

import { useModelProfiles } from "@/shared/domain/agentThread/hooks/useModelProfiles";
import { Card, Spinner } from "@/shared/foundation/primitives";

import { SectionEditLink } from "./SectionEditLink";
import styles from "./sections.module.css";

export interface SettingsSectionProps {
  agentId: string;
  /** The stored model profile; `null` runs on whatever the org's default is. */
  modelProfile: string | null;
}

/**
 * How the agent runs, one setting to a row: its name on the left, its value
 * with what that value means on the right. For now, the model it reasons
 * with.
 */
export function SettingsSection({
  agentId,
  modelProfile,
}: SettingsSectionProps) {
  const profilesQuery = useModelProfiles();
  const profiles = profilesQuery.data ?? [];
  const orgDefault =
    profiles.find((profile) => profile.is_default) ?? profiles[0];
  const chosenId = modelProfile ?? orgDefault?.id;
  // A stored id can outlive the profile it named, so the row falls back to
  // the id itself rather than reading a description off nothing.
  const profile = profiles.find((entry) => entry.id === chosenId);

  let value: ReactNode;
  if (profilesQuery.isPending) {
    value = (
      <p className={styles.loading} data-testid="agent-detail-model-loading">
        <Spinner aria-hidden />
        Loading models&hellip;
      </p>
    );
  } else {
    value = (
      <>
        <p className={styles.settingValue}>
          {profile?.label ?? chosenId ?? "The org's default"}
          {modelProfile === null && profile !== undefined && " (org default)"}
        </p>
        {profile?.description !== undefined && (
          <p className={styles.muted}>{profile.description}</p>
        )}
      </>
    );
  }

  return (
    <Card.Root size="section" data-testid="agent-detail-settings">
      <Card.Header>
        <Card.Title>Settings</Card.Title>
        <Card.Actions>
          <SectionEditLink agentId={agentId} section="model" label="model" />
        </Card.Actions>
      </Card.Header>
      <Card.Body>
        <dl className={styles.settings}>
          <div
            className={styles.setting}
            data-testid="agent-detail-model"
            data-profile={chosenId ?? ""}
            data-resolved={profile !== undefined}
          >
            <dt className={styles.settingKey}>Model</dt>
            <dd className={styles.settingBody}>{value}</dd>
          </div>
        </dl>
      </Card.Body>
    </Card.Root>
  );
}
